import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

const ACRONYMS = new Set([
  'LLC', 'LLP', 'PLLC', 'PC', 'PA', 'SC', 'LTDA',
  'CPA', 'MBA', 'CFA', 'CFP', 'JD', 'ESQ', 'PHD', 'MD',
  'CEO', 'CFO', 'COO', 'CTO', 'IRS', 'OAB', 'CRC', 'CRM',
  'II', 'III', 'IV', 'VI', 'VII', 'VIII', 'IX', 'XI', 'XII',
]);

const LOWERCASE_PARTICLES = new Set([
  'de', 'da', 'do', 'dos', 'das', 'e', 'em',
  'del', 'della', 'di', 'degli',
  'von', 'van', 'der', 'den',
  'le', 'la', 'les', 'el', 'al',
  'bin', 'ibn', 'of', 'the',
]);

const VOWELS = /[AEIOU]/;

function isShortAcronym(word: string): boolean {
  const letters = word.replace(/[^a-zA-Z]/g, '');
  if (letters.length < 2 || letters.length > 4) return false;
  if (letters !== letters.toUpperCase()) return false;
  // Explicit acronym set always wins
  if (ACRONYMS.has(letters)) return true;
  // Short ALL-CAPS with no vowels → likely acronym (LLC, GGS, TPT, CB, TV)
  // Short ALL-CAPS with vowels → likely a name (JOSE, ANNA, NETO) → not acronym
  return !VOWELS.test(letters);
}

function capitalizeWord(word: string): string {
  if (!word) return word;
  const upper = word.toUpperCase();
  if (ACRONYMS.has(upper)) return upper;
  // Short ALL-CAPS words (2-4 chars) are likely acronyms — preserve them
  if (isShortAcronym(word)) return word;
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
}

function isAllUpperOrLower(word: string): boolean {
  const letters = word.replace(/[^a-zA-ZÀ-ÿ]/g, '');
  if (!letters) return false;
  return letters === letters.toUpperCase() || letters === letters.toLowerCase();
}

export function capitalizeWords(str: string | null | undefined): string {
  if (!str) return '';

  return str
    .split(' ')
    .map((word, index) => {
      if (!word) return word;

      // Emails: preserve as lowercase
      if (word.includes('@')) return word.toLowerCase();

      // Alphanumeric codes (e.g. RL360, ASP30949444): preserve as-is
      if (/\d/.test(word) && /[a-zA-Z]/.test(word)) return word;

      // Handle hyphenated / apostrophe / dotted words
      if (/[-'.]/.test(word)) {
        return word
          .split(/([-'.])/g)
          .map((part, partIdx) => {
            if (/^[-'.]$/.test(part)) return part;
            if (!part) return part;
            const partUpper = part.toUpperCase();
            if (ACRONYMS.has(partUpper)) return partUpper;
            if (!isAllUpperOrLower(part)) return part; // mixed case like McDonald
            if (index > 0 && partIdx === 0 && LOWERCASE_PARTICLES.has(part.toLowerCase())) {
              return part.toLowerCase();
            }
            return capitalizeWord(part);
          })
          .join('');
      }

      const upperWord = word.toUpperCase();
      if (ACRONYMS.has(upperWord)) return upperWord;

      // Preserve mixed-case words (e.g. McDonald, DeVito)
      if (!isAllUpperOrLower(word)) return word;

      // Lowercase particles (except first word)
      if (index > 0 && LOWERCASE_PARTICLES.has(word.toLowerCase())) {
        return word.toLowerCase();
      }

      return capitalizeWord(word);
    })
    .join(' ');
}

export function decodeHtmlEntities(str: string | null | undefined): string {
  if (!str) return '';
  
  const textarea = document.createElement('textarea');
  textarea.innerHTML = str;
  return textarea.value;
}

export function sanitizeBio(text: string | null | undefined): string | null {
  if (!text) return null;
  
  let sanitized = text;
  
  // Decode HTML entities first
  sanitized = decodeHtmlEntities(sanitized);
  
  // Remove URLs (http, https, www)
  sanitized = sanitized.replace(/https?:\/\/[^\s]+/gi, '');
  sanitized = sanitized.replace(/www\.[^\s]+/gi, '');
  
  // Remove email addresses
  sanitized = sanitized.replace(/[\w.-]+@[\w.-]+\.\w+/gi, '');
  
  // Remove phone numbers - more specific patterns to avoid removing years/simple numbers
  // Match patterns like: +1-234-567-8900, (123) 456-7890, 123.456.7890
  sanitized = sanitized.replace(/(\+\d{1,3}[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}/g, '');
  sanitized = sanitized.replace(/\+\d{1,3}\s?\d{1,4}\s?\d{1,4}\s?\d{1,9}/g, '');
  
  // Remove lines that mention contact/website/linkedin/email
  sanitized = sanitized.split('\n').filter(line => {
    const lower = line.toLowerCase();
    return !lower.includes('you can see more') && 
           !lower.includes('contact me at') &&
           !lower.includes('reach me at') &&
           !lower.includes('find me at') &&
           !lower.includes('visit my') &&
           !lower.includes('check out my');
  }).join('\n');
  
  // Replace literal \n artifacts (WordPress migration)
  sanitized = sanitized.replace(/\\n/g, ' ');
  
  // Clean up extra whitespace
  sanitized = sanitized.replace(/\n{3,}/g, '\n\n').trim();
  
  return sanitized || null;
}

/**
 * @deprecated Use formatBytes from useLocale() for locale-aware formatting
 */
export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

/**
 * Truncate text at the last word boundary before maxLen, appending "…".
 * Returns the original string unchanged if it's already within maxLen.
 */
/**
 * Convert a relative URL to an absolute URL using the current origin.
 * Returns the URL unchanged if it's already absolute.
 */
export function toAbsoluteUrl(url: string | undefined | null): string | undefined {
  if (!url || !url.trim()) return undefined;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return `${origin}${url.startsWith('/') ? '' : '/'}${url}`;
}

export function truncateAtWord(text: string, maxLen: number): string {
  if (!text || text.length <= maxLen) return text;
  const truncated = text.slice(0, maxLen);
  const lastSpace = truncated.lastIndexOf(' ');
  return (lastSpace > 0 ? truncated.slice(0, lastSpace) : truncated) + '…';
}

export function sanitizePublicationContent(html: string | null | undefined): string {
  if (!html) return '';
  
  let sanitized = html;
  
  // Decode HTML entities
  sanitized = decodeHtmlEntities(sanitized);
  
  // Remove URLs (http, https, www)
  sanitized = sanitized.replace(/https?:\/\/[^\s<]+/gi, '');
  sanitized = sanitized.replace(/www\.[^\s<]+/gi, '');
  
  // Remove email addresses
  sanitized = sanitized.replace(/[\w.-]+@[\w.-]+\.\w+/gi, '');
  
  // Remove physical addresses (common patterns)
  sanitized = sanitized.replace(/\d+\s+[\w\s]+(?:street|st|avenue|ave|road|rd|boulevard|blvd|lane|ln|drive|dr|court|ct|circle|cir|way)[.,\s]/gi, '');
  
  // Remove empty paragraphs with non-breaking spaces or regular spaces
  sanitized = sanitized.replace(/<p[^>]*>(&nbsp;|\s|<br\s*\/?>)*<\/p>/gi, '');
  
  // Consolidate multiple consecutive paragraph breaks
  sanitized = sanitized.replace(/(<\/p>\s*){2,}(<p[^>]*>)/gi, '</p>$2');
  
  // Consolidate multiple <br> tags (more than 2 in a row)
  sanitized = sanitized.replace(/(<br\s*\/?>\s*){3,}/gi, '<br><br>');
  
  // Clean up multiple consecutive empty lines
  sanitized = sanitized.replace(/\n\s*\n\s*\n/g, '\n\n');
  
  // Strip HTML tags for plain-text display
  sanitized = sanitized.replace(/<[^>]+>/g, ' ');
  // Replace literal \n artifacts from WordPress
  sanitized = sanitized.replace(/\\n/g, ' ');
  // Replace actual newlines with spaces
  sanitized = sanitized.replace(/\n/g, ' ');
  // Collapse whitespace
  sanitized = sanitized.replace(/\s{2,}/g, ' ').trim();

  return sanitized;
}
